import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { ShieldCheck, SearchCheck, Quote } from "lucide-react";
import { toast } from "sonner";

const stats = [
  { value: 99.8, suffix: "%", label: "Shift attendance rate", decimals: 1 },
  { value: 2.4, prefix: "$", suffix: "M", label: "Paid out to guards", decimals: 1 },
  { value: 4.9, suffix: "/5", label: "Average platform rating", decimals: 1 },
  { value: 12, suffix: " min", label: "Average shift claim speed", decimals: 0 },
];

function Counter({ stat }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, stat.value, {
      duration: 1.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(v.toFixed(stat.decimals)),
    });
    return () => controls.stop();
  }, [inView, stat]);

  return (
    <div ref={ref} data-testid={`stat-${stat.label.replace(/\s+/g, "-").toLowerCase()}`}>
      <p className="font-display text-5xl font-black text-slate-50 sm:text-6xl">
        {stat.prefix}{display}{stat.suffix}
      </p>
      <p className="font-mono-tag mt-2 text-[10px] text-slate-500">{stat.label}</p>
    </div>
  );
}

const testimonials = [
  { quote: "I've been ghosted by staffing agencies for years. GuardGig28 paid me 40 minutes after my first checkout. I haven't opened another app since.", name: "Marcus T.", role: "Armed Guard · 214 shifts" },
  { quote: "We staffed a 3,000-person concert with 14 hours' notice. Every guard showed, every one was licensed. The no-show ban is why I trust it.", name: "Dana R.", role: "Ops Director, Harbor Point Arena" },
  { quote: "The two-way ratings keep everyone honest. Good venues get good guards. It really is that simple.", name: "James O.", role: "Nightclub Owner" },
];

export default function Trust() {
  const [license, setLicense] = useState("");
  const [result, setResult] = useState(null);

  const verify = (e) => {
    e.preventDefault();
    if (!license.trim()) {
      toast.error("Enter a license number to run the check.");
      return;
    }
    setResult({
      status: "VERIFIED · ACTIVE",
      detail: `Licence ${license.toUpperCase()} · SIA Door Supervisor · Register matched · Background clear`,
    });
    toast.success("Demo verification complete.");
  };

  return (
    <section id="trust" data-testid="trust-section" className="border-y border-white/5 bg-[#0a0d13] py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono-tag text-[11px] text-amber-500">Proof, not promises</p>
          <h2 className="font-display mt-4 max-w-2xl text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl">
            Built to be trusted
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-16 grid grid-cols-2 gap-x-6 gap-y-12 lg:grid-cols-4"
        >
          {stats.map((s) => <Counter key={s.label} stat={s} />)}
        </motion.div>

        <div className="mt-20 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="rounded-xl border border-amber-500/25 bg-[#0f141c] p-8 lg:col-span-5"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500">
                <SearchCheck className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <h3 className="font-display text-xl font-bold uppercase text-slate-50">Verification demo</h3>
                <p className="font-mono-tag text-[9px] text-slate-500">Simulated state-record check</p>
              </div>
            </div>
            <form onSubmit={verify} className="mt-7 flex gap-2">
              <input
                data-testid="verify-license-input"
                value={license}
                onChange={(e) => setLicense(e.target.value)}
                placeholder="Enter guard license # (e.g. G-88214)"
                className="h-12 flex-1 rounded-md border border-white/10 bg-[#07090e] px-4 text-sm text-slate-50 placeholder:text-slate-600 focus:border-amber-500/60 focus:outline-none"
              />
              <button
                data-testid="verify-license-btn"
                type="submit"
                className="h-12 rounded-md bg-amber-500 px-5 font-display text-sm font-bold uppercase text-[#07090e] transition-colors hover:bg-amber-400"
              >
                Check
              </button>
            </form>
            {result && (
              <motion.div
                data-testid="verify-license-result"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-5 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4"
              >
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-400" />
                  <span className="font-mono-tag text-[10px] text-emerald-400">{result.status}</span>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-slate-400">{result.detail}</p>
                <p className="mt-2 font-mono-tag text-[8px] text-slate-600">Demo simulation — live checks run inside the app</p>
                <a
                  data-testid="verify-sia-link"
                  href="https://services.sia.homeoffice.gov.uk/PublicRegister/SearchPublicRegister"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block font-mono-tag text-[9px] text-amber-500 underline underline-offset-4 transition-colors hover:text-amber-400"
                >
                  Verify against the real public SIA register
                </a>
              </motion.div>
            )}
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:col-span-7 lg:grid-cols-3">
            {testimonials.map((t, i) => (
              <motion.figure
                key={t.name}
                data-testid={`testimonial-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: 0.1 + i * 0.1 }}
                className="flex flex-col rounded-xl border border-white/8 bg-[#0f141c] p-7"
              >
                <Quote className="h-5 w-5 text-amber-500" fill="currentColor" />
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-300">"{t.quote}"</blockquote>
                <figcaption className="mt-5 border-t border-white/5 pt-4">
                  <p className="font-display text-sm font-bold uppercase text-slate-50">{t.name}</p>
                  <p className="font-mono-tag mt-1 text-[9px] text-slate-500">{t.role}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
